import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isFirebaseConfigured } from "@/lib/firebase";
import "@/App.css";

const Layout = lazy(() => import("@/components/Layout"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CourseCatalog = lazy(() => import("@/pages/CourseCatalog"));
const MyCourses = lazy(() => import("@/pages/MyCourses"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const CoursePlayer = lazy(() => import("@/pages/CoursePlayer"));
const Communities = lazy(() => import("@/pages/Communities"));
const CommunityDetail = lazy(() => import("@/pages/CommunityDetail"));
const Events = lazy(() => import("@/pages/Events"));
const Coaching = lazy(() => import("@/pages/Coaching"));
const InstructorDashboard = lazy(() => import("@/pages/InstructorDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Profile = lazy(() => import("@/pages/Profile"));
const Account = lazy(() => import("@/pages/Account"));
const Resources = lazy(() => import("@/pages/Resources"));
const Grades = lazy(() => import("@/pages/Grades"));
const CertificateVerify = lazy(() => import("@/pages/CertificateVerify"));
const Notes = lazy(() => import("@/pages/Notes"));
const Goals = lazy(() => import("@/pages/Goals"));
const OKRs = lazy(() => import("@/pages/OKRs"));
const LearningPaths = lazy(() => import("@/pages/LearningPaths"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Blog = lazy(() => import("@/pages/Blog"));
const Tools = lazy(() => import("@/pages/Tools"));
const CertificatePage = lazy(() => import("@/pages/CertificatePage"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Messages = lazy(() => import("@/pages/Messages"));

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }
  return children;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen" style={{ background: "var(--content-bg)" }}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center animate-pulse border-2"
          style={{ background: "var(--charcoal)", borderColor: "rgba(31,163,122,0.4)" }}>
          <span className="text-white font-heading font-bold text-sm">L↑</span>
        </div>
        <p className="text-sm" style={{ color: "var(--content-muted)" }}>Loading...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={user.role === "admin" ? "/admin" : "/"} replace /> : <AuthPage mode="login" />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        {/* Public routes — no auth required */}
        <Route path="/verify/cert/:certId" element={<CertificateVerify />} />
        <Route path="/u/:userId" element={<PublicProfile />} />

        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={user?.role === "admin" ? <Navigate to="/admin" replace /> : <Dashboard />} />
          <Route path="courses" element={<CourseCatalog />} />
          <Route path="my-courses" element={<MyCourses />} />
          <Route path="courses/:courseId" element={<CourseDetail />} />
          <Route path="courses/:courseId/learn" element={<CoursePlayer />} />
          <Route path="courses/:courseId/learn/:lessonId" element={<CoursePlayer />} />
          <Route path="communities" element={<Communities />} />
          <Route path="communities/:communityId" element={<CommunityDetail />} />
          <Route path="messages" element={<Messages />} />
          <Route path="coaching" element={<Coaching />} />
          <Route path="events" element={<Events />} />
          <Route path="notes" element={<Notes />} />
          <Route path="goals" element={<Goals />} />
          <Route path="okrs" element={<OKRs />} />
          <Route path="learning-paths" element={<LearningPaths />} />
          <Route path="resources" element={<Resources />} />
          <Route path="grades" element={<Grades />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="blog" element={<Blog />} />
          <Route path="tools" element={<Tools />} />
          <Route path="certificate/:certId" element={<CertificatePage />} />
          <Route path="profile/:userId" element={<PublicProfile />} />
          <Route
            path="instructor"
            element={
              <ProtectedRoute>
                <InstructorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route path="account" element={<Account />} />
        </Route>

        <Route path="*" element={<Navigate to={user?.role === "admin" ? "/admin" : "/"} replace />} />
      </Routes>
    </Suspense>
  );
}

function FirebaseMissing() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f1f5f9] p-8">
      <div className="max-w-md text-center space-y-2">
        <h1 className="font-heading text-xl font-bold text-slate-800">Firebase not configured</h1>
        <p className="text-sm text-[#4A5568]">
          Add the <code className="text-xs bg-white px-1 py-0.5 rounded border">REACT_APP_FIREBASE_*</code> variables
          to <code className="text-xs bg-white px-1 py-0.5 rounded border">frontend/.env.development.local</code>{" "}
          (see <code className="text-xs">.env.example</code>), then restart <code className="text-xs">npm start</code>.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  if (!isFirebaseConfigured) {
    return <FirebaseMissing />;
  }
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
